import * as React from 'react';
import { MAX_ENTRY_SECONDS, canEditRates, formatDuration, normalizeDuration, parseDuration } from '@cfl/shared';
import { DURATION_LIST_ID, DurationPresets } from './duration-presets';

import {
  Alert, AlertDescription, Button, Combobox, Dialog, DialogContent, DialogFooter,
  DialogHeader, DialogTitle, Input, Label, Textarea,
} from '@/components/cargofl-ui';
import { Hint } from '@/components/cargofl-ui';
import { fitsProject, useActivities, useProjects, useTags } from '@/lib/catalog-api';
import { ApiError } from '@/lib/api';
import { useCreateEntry, useUpdateEntry, type WeekEntry } from '@/lib/timesheet-api';
import { useSession } from '@/lib/session';

/**
 * Add or correct one entry.
 *
 * The project and activity pickers are comboboxes, not plain selects: there are 60
 * projects and 117 activities, and a native dropdown of that length is a scroll,
 * not a choice.
 */
export function EntryDialog({
  open,
  onOpenChange,
  workDate,
  entry,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workDate: string;
  entry?: WeekEntry;
}) {
  const { data: projects } = useProjects();
  const { data: activities } = useActivities();
  const create = useCreateEntry();
  const update = useUpdateEntry();

  const [projectId, setProjectId] = React.useState('');
  const [activityId, setActivityId] = React.useState('');
  const [duration, setDuration] = React.useState('');
  const [beginTime, setBeginTime] = React.useState('');
  const [hourlyRate, setHourlyRate] = React.useState('');
  const [fixedRate, setFixedRate] = React.useState('');
  const [showRates, setShowRates] = React.useState(false);
  const [description, setDescription] = React.useState('');
  const [tagIds, setTagIds] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const { data: tags } = useTags();
  const { data: session } = useSession();

  // Reset from props each time the dialog opens, so reopening it on a different
  // day or entry never shows the previous one's values.
  React.useEffect(() => {
    if (!open) return;
    setProjectId(entry?.project.id ?? '');
    setActivityId(entry?.activity.id ?? '');
    setDuration(entry?.durationSeconds ? formatDuration(entry.durationSeconds) : '');
    // An existing entry keeps the clock time it already has; a new one starts at
    // the configured time, which is what the server would have used anyway.
    setBeginTime(entry ? clockValue(entry.begin) : session?.defaultBeginTime ?? '');
    setHourlyRate(minorToInput(entry?.hourlyRateMinor ?? null));
    setFixedRate(minorToInput(entry?.fixedRateMinor ?? null));
    setShowRates(Boolean(entry?.hourlyRateMinor || entry?.fixedRateMinor));
    setDescription(entry?.description ?? '');
    setTagIds(entry?.tags.map((t) => t.id) ?? []);
    setError(null);
  }, [open, entry, session?.defaultBeginTime]);

  // An entry on a project hidden since keeps it as an option. The lists hold only
  // visible rows, so the picker came up blank and the entry could not be saved.
  const projectOptions = React.useMemo(() => {
    const options = (projects ?? []).map((p) => ({
      value: p.id,
      label: p.name,
      description: p.customer.name,
    }));
    if (entry && !options.some((o) => o.value === entry.project.id)) {
      options.unshift({ value: entry.project.id, label: entry.project.name, description: entry.project.customer.name });
    }
    return options;
  }, [projects, entry]);

  // Global activities are bookable against any project, so they stay in the list
  // whatever is selected; project-specific ones are filtered to the choice.
  const activityOptions = React.useMemo(() => {
    const options: { value: string; label: string; hint?: string }[] = (activities ?? [])
      .filter((a) => fitsProject(a, projectId, projects))
      .map((a) => ({
        value: a.id,
        label: a.name,
        hint: a.project ? undefined : 'any project',
      }));
    // Same for a hidden activity, while the entry stays on the project it was booked to.
    if (entry && projectId === entry.project.id && !options.some((o) => o.value === entry.activity.id)) {
      options.unshift({ value: entry.activity.id, label: entry.activity.name });
    }
    return options;
  }, [activities, projectId, projects, entry]);

  const busy = create.isPending || update.isPending;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const seconds = parseDuration(duration);
    if (seconds === null) {
      setError('Enter a duration like 7:30, 7.5 or 90m.');
      return;
    }
    if (seconds > MAX_ENTRY_SECONDS) {
      setError('A day cannot be longer than 24 hours.');
      return;
    }
    if (!projectId || !activityId) {
      setError('Choose a project and an activity.');
      return;
    }

    for (const [label, value] of [['hourly', hourlyRate], ['fixed', fixedRate]] as const) {
      if (value.trim() !== '' && !/^\d{1,13}(\.\d{1,2})?$/.test(value.trim())) {
        setError(`Enter the ${label} price as an amount like 1500 or 1500.50.`);
        return;
      }
    }

    const draft = {
      workDate,
      projectId,
      activityId,
      durationSeconds: seconds,
      ...(beginTime ? { beginTime } : {}),
      description: description.trim() || null,
      tagIds,
      // Empty means "no override" - sent as null so clearing one actually clears
      // it, rather than leaving the old figure in place.
      hourlyRate: hourlyRate.trim() || null,
      fixedRate: fixedRate.trim() || null,
    };

    try {
      if (entry) await update.mutateAsync({ id: entry.id, ...draft });
      else await create.mutateAsync(draft);
      onOpenChange(false);
    } catch (err) {
      // The API's message is the useful one - it names the locked date, or which
      // field was rejected. Replacing it with something generic would lose that.
      setError(err instanceof ApiError ? err.message : 'Could not save that entry.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{entry ? 'Edit entry' : 'Add time'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-muted-foreground">{longDate(workDate)}</p>

          <div className="space-y-1.5">
            <Label>Project</Label>
            <Combobox
              value={projectId || null}
              onChange={(v) => {
                setProjectId(v);
                // A project-specific activity may not survive the change, so anything
                // no longer offered is cleared rather than silently submitted.
                const stillValid = (activities ?? []).some(
                  (a) => a.id === activityId && (fitsProject(a, v, projects)),
                );
                if (!stillValid) setActivityId('');
              }}
              options={projectOptions}
              placeholder="Choose a project"
              searchPlaceholder="Search projects"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Activity</Label>
            <Combobox
              value={activityId || null}
              onChange={setActivityId}
              options={activityOptions}
              placeholder={projectId ? 'Choose an activity' : 'Choose a project first'}
              searchPlaceholder="Search activities"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <DurationPresets />
              <Label htmlFor="duration">How long</Label>
              <Input
                id="duration"
                list={DURATION_LIST_ID}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                onBlur={(e) => setDuration(normalizeDuration(e.target.value))}
                placeholder="7:30"
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              {/* The clock time the entry starts at. Every other view of this
                  entry shows it, and until now nothing could set it - so every
                  entry created here read as starting at the configured default,
                  whatever time the work actually happened. */}
              <Label htmlFor="begin-time">Started at</Label>
              <Input
                id="begin-time"
                type="time"
                value={beginTime}
                onChange={(e) => setBeginTime(e.target.value)}
              />
            </div>
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">
            7:30, 7.5, 1:20 and 90m all work — the list suggests{' '}
            {session?.durationIncrementMinutes ?? 15}-minute steps, it does not limit you.
          </p>

          {(tags?.length ?? 0) > 0 && (
            <div className="space-y-1.5">
              <Label>Tags (optional)</Label>
              {/* Toggles rather than a combobox: there are three tags, and a search
                  box over three options is more work than the options themselves. */}
              <div className="flex flex-wrap gap-1.5">
                {(tags ?? []).map((tag) => {
                  const on = tagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        setTagIds((prev) =>
                          on ? prev.filter((id) => id !== tag.id) : [...prev, tag.id],
                        )
                      }
                      className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                        on
                          ? 'border-primary bg-primary/10 font-medium text-primary'
                          : 'border-border text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {canEditRates(session?.role ?? 'EMPLOYEE') && (
            <div className="space-y-1.5">
              {/* Folded away, because almost nothing needs one: not a single entry
                  in the previous system's 24,656 carried a fixed price. But the
                  columns exist and the rate resolver honours them, so leaving no
                  way to set one made them dead weight. */}
              <Hint label={'Charge a different rate for just this entry'}><button
                type="button"
                className="text-xs text-muted-foreground underline underline-offset-2"
                onClick={() => setShowRates((v) => !v)}
              >
                {showRates ? 'Hide price override' : 'Override the price for this entry'}
              </button></Hint>
              {showRates && (
                <div className="grid gap-3 pt-1 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="hourly-rate">Hourly price</Label>
                    <Input
                      id="hourly-rate"
                      inputMode="decimal"
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(e.target.value)}
                      placeholder="from the rate tables"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fixed-rate">Fixed price</Label>
                    <Input
                      id="fixed-rate"
                      inputMode="decimal"
                      value={fixedRate}
                      onChange={(e) => setFixedRate(e.target.value)}
                      placeholder="none"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground sm:col-span-2">
                    Rupees. Empty means this entry bills at whatever the rate tables say.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="description">Note (optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : entry ? 'Save changes' : 'Add entry'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function longDate(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** The `HH:MM` an instant reads as on the viewer's clock, for a `type=time` input. */
function clockValue(iso: string): string {
  const at = new Date(iso);
  return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
}

/** Minor units to what somebody types. Kept as strings - paise never touch a float. */
function minorToInput(minor: string | null): string {
  if (minor === null) return '';
  const negative = minor.startsWith('-');
  const digits = (negative ? minor.slice(1) : minor).padStart(3, '0');
  const paise = digits.slice(-2);
  const rupees = digits.slice(0, -2);
  return `${negative ? '-' : ''}${rupees}${paise === '00' ? '' : `.${paise}`}`;
}
